import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLocation } from "react-router-dom";

/*
 * RouteFade
 * ---------
 * A 400ms champagne cross-fade overlay that briefly draws over the
 * viewport whenever the reader navigates between routes. Decoupled from
 * route mount timing so it always feels silky even on heavy pages like
 * /the-founder or /origin-graph.
 *
 * Two-layer overlay:
 *  • solid black wash that lifts scroll flash
 *  • champagne radial bloom on top for the "turning a page" feel
 *
 * Respects prefers-reduced-motion by rendering null.
 */
export default function RouteFade() {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();
  const [key, setKey] = useState(pathname);
  const [showing, setShowing] = useState(false);

  useEffect(() => {
    if (reduced) return undefined;
    if (pathname === key) return undefined;
    setShowing(true);
    setKey(pathname);
    const t = setTimeout(() => setShowing(false), 400);
    // Scroll to top on route change so the fade covers the reflow.
    window.scrollTo({ top: 0, behavior: "auto" });
    return () => clearTimeout(t);
  }, [pathname]);

  if (reduced) return null;

  return (
    <AnimatePresence>
      {showing && (
        <motion.div
          key={pathname}
          data-testid="route-fade"
          className="fixed inset-0 z-[60] pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Black wash */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{ background: "#000000" }}
          />
          {/* Champagne bloom centre */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 50% 45%, rgba(201,169,97,0.14), transparent 65%)",
              filter: "blur(20px)",
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
